import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Helmet } from "@/components/Helmet";
import { ChevronUp } from "lucide-react";

const LAST_UPDATED = "2026-01-21";

const sections = [
  { id: "overview", title: "Overview" },
  { id: "acceptable-use", title: "Acceptable use and authorisation" },
  { id: "live-scan", title: "Live Scan scope and limitations" },
  { id: "accounts", title: "Accounts, portal, plugins, and integrations" },
  { id: "reports", title: "Reports, recommendations, and remediation" },
  { id: "fair-use", title: "Fair use, rate limits, and abuse prevention" },
  { id: "liability", title: "Disclaimers and limitation of liability" },
  { id: "privacy", title: "Privacy and data handling" },
  { id: "contact", title: "Contact" },
];

const chips = [
  "Authorised use only",
  "No exploitation",
  "Rate limited",
  "Limited, external checks",
];

const Terms = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const hash = location.hash;
  const hashId = hash ? hash.replace("#", "") : "";
  const activeId = sections.some((section) => section.id === hashId) ? hashId : "overview";

  const scrollToSection = (id: string, updateHash = true) => {
    if (typeof document === "undefined") return;
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (updateHash && location.hash !== `#${id}`) {
      navigate({ pathname: location.pathname, hash: `#${id}` }, { replace: false });
    }
  };

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    if (!id) return;
    const element = document.getElementById(id);
    if (!element) return;
    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>ShadowScan Terms of Service</title>
        <meta
          name="description"
          content="ShadowScan terms of service for the website, portal, tools, scans, reports, APIs, integrations, plugins, and agents."
        />
      </Helmet>
      <Navigation />
      <main className="flex-grow pt-24" id="top">
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-20 -z-10" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent -z-10" />
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto animate-fade-in">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-6">
                <span className="font-mono text-sm text-primary">// TERMS</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">ShadowScan Terms of Service</h1>
              <p className="text-sm text-muted-foreground mb-4">
                These Terms apply to ShadowScan’s website, portal, tools, scans, reports, APIs, integrations, plugins, and agents (the “Site” and “Tools”).
              </p>
              <p className="text-xs text-muted-foreground">Last updated: {LAST_UPDATED}</p>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto rounded-xl border border-border/60 bg-secondary/20 p-6 md:p-8 space-y-4 text-sm text-muted-foreground">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <h2 className="text-lg font-heading font-semibold text-foreground">
                  Plain-English usage summary (not a substitute for the Terms)
                </h2>
                <div className="flex flex-wrap gap-2">
                  {chips.map((chip) => (
                    <span
                      key={chip}
                      className="px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs text-primary"
                    >
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                This section summarises how the service may be used and the limits that apply.
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  ShadowScan provides <strong>limited, external security checks</strong>, monitoring, and reporting. This is <strong>not</strong> a full penetration test and may not detect every issue.
                </li>
                <li>You must only scan or test systems you <strong>own</strong> or have <strong>explicit permission</strong> to scan.</li>
                <li>
                  Our Live Scan is <strong>non-intrusive</strong>: we do not brute force, exploit vulnerabilities, or bypass access controls.
                </li>
                <li>
                  Use of the Site and Tools is subject to <strong>fair-use limits</strong> and <strong>abuse prevention controls</strong> (including rate limits).
                </li>
                <li>Reports are informational and intended to support risk identification and remediation planning.</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
              <aside className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-secondary/20 p-5 hidden lg:block sticky top-28">
                  <h3 className="text-sm font-heading font-semibold text-foreground mb-4">Sections</h3>
                  <nav>
                    <ul className="space-y-3 text-sm">
                      {sections.map((section) => {
                        const isActive = activeId === section.id;
                        return (
                          <li key={section.id}>
                            <button
                              className={`w-full text-left transition-colors ${
                                isActive ? "text-primary" : "text-muted-foreground hover:text-primary"
                              }`}
                              onClick={() => scrollToSection(section.id)}
                              type="button"
                            >
                              {section.title}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                </div>

                <div className="lg:hidden">
                  <label htmlFor="terms-sections" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Sections
                  </label>
                  <select
                    id="terms-sections"
                    className="mt-2 w-full rounded-lg border border-border/60 bg-secondary/30 px-4 py-3 text-sm text-foreground"
                    value={activeId}
                    onChange={(event) => scrollToSection(event.target.value)}
                  >
                    {sections.map((section) => (
                      <option key={section.id} value={section.id}>
                        {section.title}
                      </option>
                    ))}
                  </select>
                </div>
              </aside>

              <div className="space-y-10 text-sm text-muted-foreground leading-relaxed">
                <section id="overview" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">1. Overview</h2>
                  <p>
                    ShadowScan (“we”, “us”, “our”) provides managed website security services and Tools designed to help identify security risks and suspicious signals affecting websites and WordPress environments.
                  </p>
                  <p>
                    By accessing or using the Site or any Tools, you agree to these Terms. If you do not agree, do not use the Site or Tools.
                  </p>
                  <p>You confirm that you are at least 18 years old and capable of entering into a binding agreement.</p>
                </section>

                <section id="acceptable-use" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">2. Acceptable use and authorisation</h2>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">2.1 Authorised use only</h3>
                    <p>
                      You must only use the Tools on systems you own or on systems for which you have <strong>explicit authorisation</strong> from the owner (for example, written permission from your client).
                    </p>
                    <p>
                      You are responsible for ensuring you have the right to scan, test, or assess a target. If we reasonably suspect misuse or unauthorised activity, we may restrict, suspend, or terminate access to the Site or Tools.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">2.2 Prohibited behaviour</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>attempt to gain unauthorised access to any system</li>
                      <li>use the Tools to exploit vulnerabilities or to facilitate harm</li>
                      <li>run brute-force attacks, credential stuffing, bypass attempts, or denial-of-service activity</li>
                      <li>interfere with the operation of the Site, Tools, or any third-party service</li>
                      <li>use automated means to access or scrape the Site except where explicitly permitted</li>
                    </ul>
                  </div>

                </section>

                <section id="live-scan" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">3. Live Scan scope and limitations</h2>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">3.1 What Live Scan is</h3>
                    <p>
                      Live Scan is a <strong>limited, external check</strong> based on externally observable signals and publicly accessible behaviours. It is designed to identify indicators such as common misconfigurations, exposed surface details, and signs consistent with known issues.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">3.2 What Live Scan is not</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>is not a penetration test</li>
                      <li>does not authenticate into your website</li>
                      <li>does not exploit vulnerabilities</li>
                      <li>does not brute force credentials</li>
                      <li>does not bypass access controls</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">3.3 Accuracy and completeness</h3>
                    <p>
                      Live Scan results may be incomplete, may be affected by third-party services (CDNs, WAFs, caching, hosting controls), and may change over time. Findings should be validated and interpreted within the context of your environment.
                    </p>
                  </div>

                </section>

                <section id="accounts" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">4. Accounts, portal, plugins, and integrations</h2>
                  <p>If you create an account or connect a site, you are responsible for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>maintaining the confidentiality of your access credentials and tokens</li>
                    <li>ensuring any tokens or pairing codes are used only for authorised connections</li>
                    <li>promptly notifying us of any suspected unauthorised access</li>
                  </ul>
                  <p>
                    You must not attempt to reverse engineer, disable, or circumvent security or usage controls built into the Site or Tools.
                  </p>
                </section>

                <section id="reports" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">5. Reports, recommendations, and remediation</h2>
                  <p>
                    Reports and recommendations provided by ShadowScan are for informational purposes and to assist with security decision-making and remediation planning.
                  </p>
                  <p>You acknowledge that:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>remediation actions may require changes to configuration, plugins, themes, hosting, or code</li>
                    <li>applying recommendations may have operational impacts</li>
                    <li>you remain responsible for changes made to your systems and for maintaining appropriate backups and change control</li>
                  </ul>
                  <p>
                    If you require full contractual terms for managed services, SLAs, or incident response, you should request a separate written service agreement.
                  </p>
                </section>

                <section id="fair-use" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">6. Fair use, rate limits, and abuse prevention</h2>
                  <p>
                    To protect system availability and prevent misuse, we apply fair-use controls, including (without limitation):
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>per-IP scan limits</li>
                    <li>concurrency limits</li>
                    <li>global capacity controls</li>
                    <li>automated abuse detection and blocking</li>
                  </ul>
                  <p>
                    Where limits are reached, requests may be delayed, rejected, or rate-limited. We may change these limits over time to protect the service.
                  </p>
                </section>

                <section id="liability" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">7. Disclaimers and limitation of liability</h2>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">7.1 No warranties</h3>
                    <p>To the maximum extent permitted by law, the Site and Tools are provided on an “as is” and “as available” basis. We do not warrant that:</p>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>the Tools will detect all vulnerabilities, malware, or security issues</li>
                      <li>the Tools will be uninterrupted or error-free</li>
                      <li>any findings will be complete, current, or suitable for a particular purpose</li>
                    </ul>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-base font-semibold text-foreground">7.2 Limitation of liability</h3>
                    <p>
                      To the maximum extent permitted by law, we are not liable for indirect, incidental, special, consequential, or punitive losses, including loss of profits, revenue, data, reputation, or business interruption arising from or related to your use of the Site or Tools.
                    </p>
                    <p>
                      Nothing in these Terms excludes, restricts, or modifies any right or remedy you may have under the <strong>Australian Consumer Law</strong> that cannot be excluded, restricted, or modified.
                    </p>
                  </div>

                </section>

                <section id="privacy" className="scroll-mt-28 border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">8. Privacy and data handling</h2>
                  <p>
                    Our handling of personal information is described in our <Link className="text-primary hover:underline" to="/privacy">Privacy Policy</Link>.
                  </p>
                  <p>
                    We may collect limited technical metadata required to operate and secure the Tools (for example, request timestamps, performance indicators, and abuse-prevention signals). We aim to minimise data collection, redact sensitive values where feasible, and retain data only as long as necessary for operational and security purposes.
                  </p>
                  <p>If you use third-party integrations, their terms and privacy practices may also apply.</p>
                  <p>
                    Subprocessors (where applicable): <Link className="text-primary hover:underline" to="/subprocessors">see subprocessors</Link>.
                  </p>
                </section>

                <section className="border-b border-border/60 pb-8 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">9. Changes to these Terms</h2>
                  <p>
                    We may update these Terms from time to time. The “Last updated” date indicates when changes were made. Your continued use of the Site or Tools after updates means you accept the updated Terms.
                  </p>
                </section>

                <section id="contact" className="scroll-mt-28 space-y-4">
                  <h2 className="text-2xl font-heading font-semibold text-foreground">10. Contact</h2>
                  <p>For questions about these Terms or to request contractual managed-service terms, contact:</p>
                  <p className="text-foreground font-semibold">ShadowScan</p>
                  <p>
                    Email: <a className="text-primary hover:underline" href="mailto:support@shadowscan.com.au">support@shadowscan.com.au</a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>
        <button
          className={`fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full border border-border/70 bg-secondary/80 text-foreground shadow-lg backdrop-blur transition duration-200 hover:text-primary ${
            showBackToTop ? "opacity-100 scale-100" : "pointer-events-none opacity-0 scale-95"
          }`}
          type="button"
          onClick={() => scrollToSection("top", false)}
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
